<template>
	<div v-if="groups.length > 0" class="search-results">
		<div v-for="group in groups" :key="group.key" class="search-results-group">
			<div class="search-results-label">
				<i
					v-if="group.icon"
					:class="[group.icon, 'search-results-label-icon']"
				></i>
				{{ group.label }}
			</div>
			<div v-if="group.failed" class="search-results-failed">
				Search failed.
			</div>
			<template v-else>
				<div
					v-for="result in group.results"
					:key="result.key"
					class="search-result"
					role="button"
					tabindex="0"
					@click="$emit('select', result)"
					@keydown.enter.prevent="$emit('select', result)"
					@keydown.space.prevent="$emit('select', result)"
				>
					<div class="search-result-line">
						<i
							v-if="result.icon"
							:class="[result.icon, 'search-result-icon']"
						></i>
						<span class="search-result-title">{{ result.title }}</span>
						<span v-if="result.badge" class="search-result-badge">{{
							result.badge
						}}</span>
						<span
							v-if="result.tier || result.secondaryAction"
							class="search-result-trailing"
						>
							<span v-if="result.tier" class="search-result-tier">{{
								result.tier
							}}</span>
							<button
								v-if="result.secondaryAction"
								type="button"
								class="search-result-secondary"
								:title="result.secondaryAction.label"
								:aria-label="result.secondaryAction.label"
								@click.stop="$emit('secondary', result)"
								@keydown.enter.stop.prevent="$emit('secondary', result)"
								@keydown.space.stop.prevent="$emit('secondary', result)"
							>
								<i
									v-if="result.secondaryAction.icon"
									:class="result.secondaryAction.icon"
								></i>
								<template v-else>{{ result.secondaryAction.label }}</template>
							</button>
						</span>
					</div>
					<div v-if="result.detail" class="search-result-detail">
						{{ result.detail }}
					</div>
				</div>
				<div
					v-if="group.total !== undefined && group.total > group.results.length"
					class="search-results-more"
				>
					{{ (group.total - group.results.length).toLocaleString() }} more
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
// The one result list both search surfaces draw.
//
// It renders groups of rows and nothing else: a heading per group, one compact
// row per hit, the "N more" line when the caller fetched only part of a group's
// matches, and the failure line in place of a group's rows when that group's
// request failed. A whole-empty list renders nothing at all — no border, no
// padding — so the pane can drop the list into its layout without guarding it
// and the empty state stays the pane's own message.
//
// `payload` is opaque here by design. The component never reads it, performs no
// I/O and touches no map; every action leaves through `select` (the row was
// chosen) and `secondary` (the row's secondary action was chosen), and the
// owning pane binds the payload back to the type it put in.
import type { SearchGroup, SearchResult } from "./types";

defineProps<{ groups: SearchGroup[] }>();

defineEmits<{
	select: [result: SearchResult];
	secondary: [result: SearchResult];
}>();
</script>

<style scoped>
/* One bordered card holding every group. The rows keep the panes' compact
   type — a 0.8125rem title over a 0.6875rem detail — because the list shares
   the rail with the ledger's chrome and must not outgrow it. */
.search-results {
	border: 1px solid #dee2e6;
	border-radius: 0.25rem;
	background: #fff;
	overflow: hidden;
}

.search-results-label {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	padding: 4px 8px 2px;
	background: #f8f9fa;
	border-bottom: 1px solid #f1f3f5;
	color: #495057;
	font-size: 0.6875rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}

.search-results-label-icon {
	font-size: 0.75rem;
}

.search-results-failed {
	padding: 4px 8px;
	color: #c92a2a;
	font-size: 0.75rem;
}

.search-results-more {
	padding: 2px 8px 4px;
	color: #adb5bd;
	font-size: 0.6875rem;
}

.search-result {
	padding: 4px 8px;
	border-bottom: 1px solid #f1f3f5;
	line-height: 1.35;
	cursor: pointer;
}

.search-result:last-child {
	border-bottom: 0;
}

.search-result:hover,
.search-result:focus-visible {
	background: #f1f3f5;
	outline: none;
}

.search-result-line {
	display: flex;
	align-items: center;
	gap: 0.35rem;
	min-width: 0;
}

.search-result-icon {
	flex: none;
	color: #6c757d;
	font-size: 0.75rem;
}

.search-result-title {
	flex: 0 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 0.8125rem;
	font-weight: 600;
}

.search-result-badge {
	flex: none;
	font-size: 0.6875rem;
	font-weight: 600;
	color: #0d6efd;
	background: rgba(13, 110, 253, 0.1);
	border-radius: 999px;
	padding: 0.05rem 0.45rem;
}

.search-result-trailing {
	display: flex;
	align-items: center;
	gap: 0.35rem;
	flex: none;
	margin-left: auto;
}

.search-result-tier {
	color: #adb5bd;
	font-size: 0.6875rem;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}

.search-result-secondary {
	border: 0;
	background: transparent;
	color: #6c757d;
	font-size: 0.6875rem;
	padding: 0 0.2rem;
	cursor: pointer;
}

.search-result-secondary:hover {
	color: #0d6efd;
}

.search-result-detail {
	color: #6c757d;
	font-size: 0.6875rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
